import type { L10n } from './i18n';

/** UI rang ohangi — dizayn tizimida tokenlarga bog'langan. */
export type Tone = 'neutral' | 'info' | 'accent' | 'success' | 'warning' | 'danger' | 'violet';

export interface StatusDef extends L10n {
  tone: Tone;
  /** Yakuniy holat (keyin o'zgarmaydi). */
  final?: boolean;
  /** Salbiy natija (bekor, qaytarish, rad). */
  negative?: boolean;
}

type StatusMap<K extends string> = Record<K, StatusDef>;
const def = <K extends string>(m: StatusMap<K>) => m;

// ---------------------------------------------------------------- Buyurtma (OMS)
export const ORDER_STATUSES = def({
  new: { uz: 'Yangi', ru: 'Новый', en: 'New', zh: '新建', tone: 'info' },
  on_hold: { uz: 'Kutilmoqda', ru: 'Ожидание', en: 'On hold', zh: '挂起', tone: 'warning' },
  confirmed: { uz: 'Tasdiqlandi', ru: 'Подтверждён', en: 'Confirmed', zh: '已确认', tone: 'accent' },
  /*
   * YIG'ISHGA TAYYOR — buyurtma ombor ishiga O'TDI, lekin hali yig'ilmayapti.
   * Stocker'dagi `crmready` shu holat: CRM buyurtmasi `crmorder` ga kelganda "Yangi",
   * `crmready` ga kelganda shu holatga o'tadi. Ombor navbati aynan shu holatni kutadi.
   *
   * `picking` dan farqi: `picking` ni PARTIYA qo'yadi (yig'ish boshlanganda), bu holatni
   * esa savdo tomoni qo'yadi — "meniki tayyor, olib ketsa bo'ladi".
   */
  ready_to_pick: { uz: 'Yig‘ishga tayyor', ru: 'Готов к сборке', en: 'Ready to pick', zh: '待拣货', tone: 'violet' },
  picking: { uz: 'Yig‘ilmoqda', ru: 'Сборка', en: 'Picking', zh: '拣货中', tone: 'violet' },
  packed: { uz: 'Qadoqlandi', ru: 'Упакован', en: 'Packed', zh: '已打包', tone: 'success' },
  shipped: { uz: 'Jo‘natildi', ru: 'Отгружен', en: 'Shipped', zh: '已发出', tone: 'info' },
  delivering: { uz: 'Yetkazilmoqda', ru: 'Доставляется', en: 'Out for delivery', zh: '配送中', tone: 'accent' },
  delivered: { uz: 'Yetkazildi', ru: 'Доставлен', en: 'Delivered', zh: '已送达', tone: 'success', final: true },
  returning: { uz: 'Qaytmoqda', ru: 'Возвращается', en: 'Returning', zh: '退回中', tone: 'warning', negative: true },
  returned: { uz: 'Qaytarildi', ru: 'Возвращён', en: 'Returned', zh: '已退回', tone: 'danger', final: true, negative: true },
  canceled: { uz: 'Bekor qilindi', ru: 'Отменён', en: 'Cancelled', zh: '已取消', tone: 'danger', final: true, negative: true },
});
export type OrderStatus = keyof typeof ORDER_STATUSES;

/** Ruxsat etilgan o'tishlar (server tekshiradi). */
export const ORDER_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  new: ['on_hold', 'confirmed', 'canceled'],
  on_hold: ['confirmed', 'canceled'],
  confirmed: ['ready_to_pick', 'picking', 'on_hold', 'canceled'],
  ready_to_pick: ['picking', 'confirmed', 'on_hold', 'canceled'],
  picking: ['packed', 'ready_to_pick', 'confirmed', 'canceled'],
  packed: ['shipped', 'picking', 'canceled'],
  shipped: ['delivering', 'delivered', 'returning', 'canceled'],
  delivering: ['delivered', 'returning', 'shipped'],
  delivered: ['returning'],
  returning: ['returned'],
  returned: [],
  canceled: [],
};

export const FULFILLMENT_TYPES = def({
  fbs: { uz: 'FBS (marketplace kuryeri)', ru: 'FBS', en: 'FBS (marketplace courier)', zh: 'FBS（平台配送）', tone: 'violet' },
  fbo: { uz: 'FBO (marketplace ombori)', ru: 'FBO', en: 'FBO (marketplace warehouse)', zh: 'FBO（平台仓储）', tone: 'info' },
  dbs: { uz: 'O‘z kuryerimiz', ru: 'Своя доставка', en: 'Our own courier', zh: '自有配送', tone: 'accent' },
  carrier: { uz: 'Hamkor kuryer (EMU)', ru: 'Курьерская служба', en: 'Partner carrier', zh: '第三方快递', tone: 'neutral' },
  pickup: { uz: 'Olib ketish', ru: 'Самовывоз', en: 'Pickup', zh: '自提', tone: 'neutral' },
});
export type FulfillmentType = keyof typeof FULFILLMENT_TYPES;

export const SALES_CHANNELS = def({
  uzum: { uz: 'Uzum Market', ru: 'Uzum Market', en: 'Uzum Market', zh: 'Uzum Market', tone: 'violet' },
  own_site: { uz: 'Sayt', ru: 'Сайт', en: 'Website', zh: '官网', tone: 'info' },
  retailcrm: { uz: 'RetailCRM', ru: 'RetailCRM', en: 'RetailCRM', zh: 'RetailCRM', tone: 'accent' },
  telegram: { uz: 'Telegram', ru: 'Telegram', en: 'Telegram', zh: 'Telegram', tone: 'info' },
  call_center: { uz: 'Call-markaz', ru: 'Колл-центр', en: 'Call centre', zh: '客服中心', tone: 'accent' },
  manual: { uz: 'Qo‘lda', ru: 'Вручную', en: 'Manual', zh: '手动', tone: 'neutral' },
  wildberries: { uz: 'Wildberries', ru: 'Wildberries', en: 'Wildberries', zh: 'Wildberries', tone: 'violet' },
  yandex: { uz: 'Yandex Market', ru: 'Яндекс Маркет', en: 'Yandex Market', zh: 'Yandex Market', tone: 'warning' },
});
export type SalesChannel = keyof typeof SALES_CHANNELS;

export const PAYMENT_METHODS = def({
  cash: { uz: 'Naqd', ru: 'Наличные', en: 'Cash', zh: '现金', tone: 'success' },
  card: { uz: 'Karta', ru: 'Карта', en: 'Card', zh: '银行卡', tone: 'info' },
  online: { uz: 'Onlayn', ru: 'Онлайн', en: 'Online', zh: '在线支付', tone: 'accent' },
  marketplace: { uz: 'Marketplace orqali', ru: 'Через маркетплейс', en: 'Via marketplace', zh: '平台代收', tone: 'violet' },
  transfer: { uz: 'Pul o‘tkazma', ru: 'Перечисление', en: 'Bank transfer', zh: '银行转账', tone: 'neutral' },
});
export type PaymentMethod = keyof typeof PAYMENT_METHODS;

export const PAYMENT_STATUSES = def({
  unpaid: { uz: 'To‘lanmagan', ru: 'Не оплачен', en: 'Unpaid', zh: '未付款', tone: 'warning' },
  partial: { uz: 'Qisman', ru: 'Частично', en: 'Partial', zh: '部分付款', tone: 'warning' },
  paid: { uz: 'To‘langan', ru: 'Оплачен', en: 'Paid', zh: '已付款', tone: 'success', final: true },
  refunded: { uz: 'Qaytarilgan', ru: 'Возвращён', en: 'Refunded', zh: '已退款', tone: 'danger', final: true, negative: true },
});
export type PaymentStatus = keyof typeof PAYMENT_STATUSES;

// ---------------------------------------------------------------- Katalog
export const PRODUCT_STATUSES = def({
  draft: { uz: 'Qoralama', ru: 'Черновик', en: 'Draft', zh: '草稿', tone: 'neutral' },
  moderation: { uz: 'Moderatsiyada', ru: 'На модерации', en: 'In moderation', zh: '审核中', tone: 'warning' },
  active: { uz: 'Faol', ru: 'Активен', en: 'Active', zh: '已上架', tone: 'success' },
  rejected: { uz: 'Rad etildi', ru: 'Отклонён', en: 'Rejected', zh: '已驳回', tone: 'danger', negative: true },
  archived: { uz: 'Arxivda', ru: 'В архиве', en: 'Archived', zh: '已归档', tone: 'neutral', final: true },
});
export type ProductStatus = keyof typeof PRODUCT_STATUSES;

// ---------------------------------------------------------------- Hamkor
export const SELLER_STATUSES = def({
  applicant: { uz: 'Ariza', ru: 'Заявка', en: 'Application', zh: '申请中', tone: 'info' },
  onboarding: { uz: 'Ulanmoqda', ru: 'Подключение', en: 'Onboarding', zh: '接入中', tone: 'warning' },
  active: { uz: 'Faol', ru: 'Активен', en: 'Active', zh: '正常', tone: 'success' },
  suspended: { uz: 'To‘xtatilgan', ru: 'Приостановлен', en: 'Suspended', zh: '已暂停', tone: 'danger', negative: true },
  rejected: { uz: 'Rad etilgan', ru: 'Отклонён', en: 'Rejected', zh: '已拒绝', tone: 'danger', final: true, negative: true },
});
export type SellerStatus = keyof typeof SELLER_STATUSES;

// ---------------------------------------------------------------- Ombor (WMS)
export const SUPPLY_STATUSES = def({
  draft: { uz: 'Qoralama', ru: 'Черновик', en: 'Draft', zh: '草稿', tone: 'neutral' },
  submitted: { uz: 'Yuborildi', ru: 'Отправлена', en: 'Submitted', zh: '已提交', tone: 'info' },
  arrived: { uz: 'Omborga keldi', ru: 'Прибыла', en: 'Arrived', zh: '已到仓', tone: 'accent' },
  receiving: { uz: 'Qabul qilinmoqda', ru: 'Приёмка', en: 'Receiving', zh: '收货中', tone: 'violet' },
  accepted: { uz: 'Qabul qilindi', ru: 'Принята', en: 'Accepted', zh: '已收货', tone: 'success', final: true },
  discrepancy: { uz: 'Farq bilan qabul', ru: 'Принята с расхождением', en: 'Accepted with discrepancy', zh: '差异收货', tone: 'warning', final: true },
  canceled: { uz: 'Bekor qilindi', ru: 'Отменена', en: 'Cancelled', zh: '已取消', tone: 'danger', final: true, negative: true },
});
export type SupplyStatus = keyof typeof SUPPLY_STATUSES;

export const BATCH_STATUSES = def({
  open: { uz: 'Ochiq', ru: 'Открыта', tone: 'info' },
  picking: { uz: 'Yig‘ilmoqda', ru: 'Сборка', en: 'Picking', zh: '拣货中', tone: 'violet' },
  packing: { uz: 'Qadoqlanmoqda', ru: 'Упаковка', en: 'Packing', zh: '打包中', tone: 'accent' },
  closed: { uz: 'Yopildi', ru: 'Закрыта', en: 'Closed', zh: '已关闭', tone: 'success', final: true },
});
export type BatchStatus = keyof typeof BATCH_STATUSES;

export const PARCEL_STATUSES = def({
  packed: { uz: 'Qadoqlandi', ru: 'Упакована', en: 'Packed', zh: '已打包', tone: 'success' },
  dispatched: { uz: 'Topshirildi', ru: 'Отгружена', en: 'Dispatched', zh: '已出库', tone: 'info' },
  handed_over: { uz: 'Kuryerga berildi', ru: 'Передана', en: 'Handed to courier', zh: '已交配送员', tone: 'violet', final: true },
  returned_out: { uz: 'Qaytarilmoqda', ru: 'Возвращается', en: 'Returning', zh: '退回中', tone: 'warning', negative: true },
  returned: { uz: 'Omborga qaytdi', ru: 'Возвращена на склад', en: 'Returned to warehouse', zh: '已退回仓库', tone: 'danger', final: true, negative: true },
});
export type ParcelStatus = keyof typeof PARCEL_STATUSES;

export const MOVEMENT_TYPES = def({
  receipt: { uz: 'Qabul', ru: 'Приёмка', en: 'Receipt', zh: '收货', tone: 'success' },
  putaway: { uz: 'Joylashtirish', ru: 'Размещение', en: 'Put-away', zh: '上架', tone: 'info' },
  move: { uz: 'Ko‘chirish', ru: 'Перемещение', en: 'Transfer', zh: '移库', tone: 'neutral' },
  reserve: { uz: 'Band qilish', ru: 'Резерв', en: 'Reserve', zh: '预留', tone: 'accent' },
  unreserve: { uz: 'Band bekor', ru: 'Снятие резерва', en: 'Release reserve', zh: '解除预留', tone: 'neutral' },
  pick: { uz: 'Yig‘ish', ru: 'Отбор', en: 'Pick', zh: '拣货', tone: 'violet' },
  ship: { uz: 'Chiqim (jo‘natish)', ru: 'Отгрузка', en: 'Ship out', zh: '出库', tone: 'warning' },
  return: { uz: 'Qaytish', ru: 'Возврат', en: 'Return', zh: '退货入库', tone: 'info' },
  adjust_plus: { uz: 'Inventarizatsiya (+)', ru: 'Инвентаризация (+)', en: 'Stocktake (+)', zh: '盘盈', tone: 'success' },
  adjust_minus: { uz: 'Inventarizatsiya (−)', ru: 'Инвентаризация (−)', en: 'Stocktake (−)', zh: '盘亏', tone: 'danger' },
  write_off: { uz: 'Hisobdan chiqarish', ru: 'Списание', en: 'Write-off', zh: '报损', tone: 'danger' },
});
export type MovementType = keyof typeof MOVEMENT_TYPES;

export const INVENTORY_STATUSES = def({
  planned: { uz: 'Rejada', ru: 'Запланирована', en: 'Planned', zh: '已计划', tone: 'neutral' },
  counting: { uz: 'Sanalmoqda', ru: 'Пересчёт', en: 'Counting', zh: '盘点中', tone: 'violet' },
  review: { uz: 'Tekshiruvda', ru: 'На проверке', en: 'Under review', zh: '复核中', tone: 'warning' },
  applied: { uz: 'Qo‘llandi', ru: 'Проведена', en: 'Applied', zh: '已过账', tone: 'success', final: true },
  canceled: { uz: 'Bekor', ru: 'Отменена', en: 'Cancelled', zh: '已取消', tone: 'danger', final: true, negative: true },
});
export type InventoryStatus = keyof typeof INVENTORY_STATUSES;

// ---------------------------------------------------------------- Yetkazib berish (DMS)
/**
 * Kuryer darajasidagi holatlar — DMS v8 statuslarining umumlashtirilgani.
 *
 * «Yo'qolgan» bu yerda YO'Q: DMS 0069 dan beri u STATUS EMAS, BELGI (`delivery.tasks.lostAt` +
 * `lostComment`) — status buyurtmaning HAQIQIY holatini (kimda, qaysi bosqichda qolgani)
 * ko'rsatib turishi kerak, belgi esa uning ustiga qo'yiladi va topshiriq yakuniy holatga o'tib
 * kuryerdan qabul qilinganda o'zi olinadi.
 */
export const DELIVERY_STATUSES = def({
  pending: { uz: 'Navbatda', ru: 'В очереди', en: 'Queued', zh: '待分配', tone: 'neutral' },
  assigned: { uz: 'Kuryer qabul qildi', ru: 'Курьер принял', en: 'Courier accepted', zh: '配送员已接单', tone: 'info' },
  on_the_way: { uz: 'Yo‘lda', ru: 'В пути', en: 'On the way', zh: '途中', tone: 'accent' },
  delivered: { uz: 'Yetkazildi', ru: 'Доставлен', en: 'Delivered', zh: '已送达', tone: 'success', final: true },
  partial: { uz: 'Yetkazildi-Narx o‘zgardi', ru: 'Доставлен-Изменена цена', en: 'Delivered – price changed', zh: '已送达（价格变更）', tone: 'success', final: true },
  callback: { uz: 'Qayta qo‘ng‘iroq', ru: 'Перезвон', en: 'Call back', zh: '需回电', tone: 'warning' },
  rescheduled: { uz: 'Boshqa kunga', ru: 'Другой день', en: 'Rescheduled', zh: '改期', tone: 'warning' },
  transfer: { uz: 'Boshqa kuryerga', ru: 'Передача', en: 'Transferred', zh: '转交他人', tone: 'violet' },
  rejected: { uz: 'Rad etildi', ru: 'Отказ', en: 'Refused', zh: '客户拒收', tone: 'danger', final: true, negative: true },
  canceled: { uz: 'Bekor qilindi', ru: 'Отменён', en: 'Cancelled', zh: '已取消', tone: 'danger', final: true, negative: true },
});
export type DeliveryStatus = keyof typeof DELIVERY_STATUSES;

export const COURIER_DAY_STATUSES = def({
  open: { uz: 'Ochiq (skan)', ru: 'Открыт (скан)', tone: 'info' },
  printed: { uz: 'Marshrut chop etildi', ru: 'Маршрут распечатан', en: 'Route printed', zh: '路线已打印', tone: 'violet' },
  assigned: { uz: 'Biriktirildi', ru: 'Назначен', en: 'Assigned', zh: '已分配', tone: 'accent' },
  settling: { uz: 'Hisob-kitob', ru: 'Расчёт', en: 'Settling', zh: '结算中', tone: 'warning' },
  closed: { uz: 'Yopildi', ru: 'Закрыт', en: 'Closed', zh: '已关闭', tone: 'success', final: true },
});
export type CourierDayStatus = keyof typeof COURIER_DAY_STATUSES;

// ---------------------------------------------------------------- Moliya
export const PAYOUT_STATUSES = def({
  requested: { uz: 'So‘raldi', ru: 'Запрошена', en: 'Requested', zh: '已申请', tone: 'info' },
  approved: { uz: 'Tasdiqlandi', ru: 'Одобрена', en: 'Approved', zh: '已批准', tone: 'accent' },
  paid: { uz: 'To‘landi', ru: 'Выплачена', en: 'Paid', zh: '已支付', tone: 'success', final: true },
  rejected: { uz: 'Rad etildi', ru: 'Отклонена', en: 'Rejected', zh: '已驳回', tone: 'danger', final: true, negative: true },
});
export type PayoutStatus = keyof typeof PAYOUT_STATUSES;

export const MONEY_REQUEST_STATUSES = def({
  pending: { uz: 'Kutilmoqda', ru: 'Ожидает', en: 'Pending', zh: '待处理', tone: 'warning' },
  approved: { uz: 'Tasdiqlandi', ru: 'Подтверждено', en: 'Approved', zh: '已批准', tone: 'accent' },
  completed: { uz: 'Yakunlandi', ru: 'Завершено', en: 'Completed', zh: '已完成', tone: 'success', final: true },
  rejected: { uz: 'Rad etildi', ru: 'Отклонено', en: 'Rejected', zh: '已驳回', tone: 'danger', final: true, negative: true },
});
export type MoneyRequestStatus = keyof typeof MONEY_REQUEST_STATUSES;

export const FISCAL_RECEIPT_STATUSES = def({
  pending: { uz: 'Navbatda', ru: 'В очереди', en: 'Queued', zh: '排队中', tone: 'neutral' },
  sent: { uz: 'Yuborildi', ru: 'Отправлен', en: 'Sent', zh: '已发送', tone: 'info' },
  fiscalized: { uz: 'Fiskallashtirildi', ru: 'Фискализирован', en: 'Fiscalised', zh: '已开票', tone: 'success', final: true },
  failed: { uz: 'Xato', ru: 'Ошибка', en: 'Failed', zh: '失败', tone: 'danger', negative: true },
  canceled: { uz: 'Bekor', ru: 'Отменён', en: 'Cancelled', zh: '已作废', tone: 'neutral', final: true, negative: true },
});
export type FiscalReceiptStatus = keyof typeof FISCAL_RECEIPT_STATUSES;

// ---------------------------------------------------------------- HR
export const EMPLOYEE_STATUSES = def({
  active: { uz: 'Ishlamoqda', ru: 'Работает', en: 'Working', zh: '在职', tone: 'success' },
  probation: { uz: 'Sinov muddati', ru: 'Испытательный срок', en: 'Probation', zh: '试用期', tone: 'info' },
  on_leave: { uz: 'Ta‘tilda', ru: 'В отпуске', en: 'On leave', zh: '休假中', tone: 'warning' },
  dismissed: { uz: 'Bo‘shatilgan', ru: 'Уволен', en: 'Dismissed', zh: '已离职', tone: 'neutral', final: true, negative: true },
});
export type EmployeeStatus = keyof typeof EMPLOYEE_STATUSES;

export const ATTENDANCE_STATUSES = def({
  done: { uz: 'Keldi-ketdi', ru: 'Отработал', en: 'Full day', zh: '已打卡', tone: 'success' },
  open: { uz: 'Chiqmagan', ru: 'Не отметил уход', tone: 'warning' },
  absent: { uz: 'Kelmadi', ru: 'Отсутствовал', en: 'Absent', zh: '缺勤', tone: 'danger', negative: true },
  error: { uz: 'Skan xatosi', ru: 'Ошибка отметки', en: 'Scan error', zh: '打卡异常', tone: 'danger' },
  rest: { uz: 'Dam olish', ru: 'Выходной', en: 'Day off', zh: '休息日', tone: 'neutral' },
  leave: { uz: 'Ta‘til', ru: 'Отпуск', en: 'Leave', zh: '休假', tone: 'info' },
  holiday: { uz: 'Bayram', ru: 'Праздник', en: 'Holiday', zh: '节假日', tone: 'violet' },
  unset: { uz: 'Jadval yo‘q', ru: 'Без графика', en: 'No schedule', zh: '无排班', tone: 'neutral' },
});
export type AttendanceStatus = keyof typeof ATTENDANCE_STATUSES;

// ---------------------------------------------------------------- Call-markaz
export const CHAT_STATUSES = def({
  bot: { uz: 'Botda', ru: 'У бота', en: 'With the bot', zh: '机器人接待', tone: 'neutral' },
  queued: { uz: 'Navbatda', ru: 'В очереди', en: 'Queued', zh: '排队中', tone: 'warning' },
  active: { uz: 'Faol', ru: 'Активен', en: 'Active', zh: '进行中', tone: 'accent' },
  closed: { uz: 'Yopilgan', ru: 'Закрыт', en: 'Closed', zh: '已关闭', tone: 'success', final: true },
});
export type ChatStatus = keyof typeof CHAT_STATUSES;

export const ORDER_REQUEST_STATUSES = def({
  pending: { uz: 'Kutilmoqda', ru: 'Ожидает', en: 'Pending', zh: '待处理', tone: 'warning' },
  processing: { uz: 'Jarayonda', ru: 'В обработке', en: 'Processing', zh: '处理中', tone: 'info' },
  approved: { uz: 'Tasdiqlandi', ru: 'Одобрен', en: 'Approved', zh: '已通过', tone: 'success', final: true },
  rejected: { uz: 'Rad etildi', ru: 'Отклонён', en: 'Rejected', zh: '已驳回', tone: 'danger', final: true, negative: true },
  failed: { uz: 'Xato', ru: 'Ошибка', en: 'Failed', zh: '失败', tone: 'danger', negative: true },
});
export type OrderRequestStatus = keyof typeof ORDER_REQUEST_STATUSES;

/** Barcha status guruhlari — UI `StatusPill` shu orqali rang va nomni topadi. */
export const STATUS_GROUPS = {
  order: ORDER_STATUSES,
  fulfillment: FULFILLMENT_TYPES,
  channel: SALES_CHANNELS,
  paymentMethod: PAYMENT_METHODS,
  payment: PAYMENT_STATUSES,
  product: PRODUCT_STATUSES,
  seller: SELLER_STATUSES,
  supply: SUPPLY_STATUSES,
  batch: BATCH_STATUSES,
  parcel: PARCEL_STATUSES,
  movement: MOVEMENT_TYPES,
  inventory: INVENTORY_STATUSES,
  delivery: DELIVERY_STATUSES,
  courierDay: COURIER_DAY_STATUSES,
  payout: PAYOUT_STATUSES,
  moneyRequest: MONEY_REQUEST_STATUSES,
  fiscal: FISCAL_RECEIPT_STATUSES,
  employee: EMPLOYEE_STATUSES,
  attendance: ATTENDANCE_STATUSES,
  chat: CHAT_STATUSES,
  orderRequest: ORDER_REQUEST_STATUSES,
} as const;
export type StatusGroup = keyof typeof STATUS_GROUPS;

export function statusDef(group: StatusGroup, code: string | null | undefined): StatusDef {
  const map = STATUS_GROUPS[group] as Record<string, StatusDef>;
  return (code && map[code]) || { uz: code ?? '—', ru: code ?? '—', tone: 'neutral' };
}

export function statusOptions(group: StatusGroup) {
  return Object.entries(STATUS_GROUPS[group] as Record<string, StatusDef>).map(([value, d]) => ({ value, ...d }));
}
